import { useState, useEffect, FC } from "react";
import { useSettingsContext } from "../../../Settings/hooks/useSettingsContext";
import { useTranslation } from "react-i18next";

import { 
  MenuItem, Select, FormControl, InputLabel, Box, Grid,
  SelectChangeEvent,
  Skeleton
} from "@mui/material";
import { default as contractSelectService } from "../services/contractSelect";
import { ContractListInfo, ContractInfo, AvailableContractTypes, ContractSelect } from "../types/contractSelectFields";

export interface ContractSelectFieldParams {
  onChange: (newValue: ContractSelect) => void;
  value?: ContractSelect;
  hideIfEmpty?: boolean;
  readOnly?: boolean;
}

const ContractSelectField: FC<ContractSelectFieldParams> = ({ 
  onChange,
  value = undefined,
  hideIfEmpty = false,
  readOnly = false, 
}) => {

  const Settings = useSettingsContext();
  const { t } = useTranslation();

  const [ state, setState ] = useState<"fetching" | "success" | "error">("fetching");
  const [ availableItems, setAvailableItems ] = useState<ContractListInfo["result"] | undefined>();
  const [ selectedItem, setSelectedItem ] = useState<ContractInfo | undefined>();
  const [ contractType, setContractType ] = useState<AvailableContractTypes>(Settings.features?.displayTypes[0] as AvailableContractTypes);
  
  const handleChange = (event: SelectChangeEvent) => {
    const newSelectedItem = availableItems![contractType]!.find(item => item.id.toString() == event.target.value)!;
    setSelectedItem(newSelectedItem);
    onChange({...newSelectedItem, type: contractType});
  }

  const changeContractType = (event: SelectChangeEvent) => {
    const value = event.target.value as AvailableContractTypes;
    const newSelectedItem = availableItems![value]![0];
    setContractType(value);
    setSelectedItem(newSelectedItem);
    onChange({...newSelectedItem, type: value});
  }

  useEffect(() => {
    setState("fetching");

    contractSelectService.getContractList()
      .then(response => {
        const newItems = response.data?.result;
        if (!newItems) return;

        setState("success");

        const newContractType = (Object.keys(newItems) as AvailableContractTypes[])
          .find(type => Array.isArray(newItems[type]) && newItems[type].length > 0 && Settings.features?.displayTypes.includes(type));

        if (!newContractType) return;

        const newSelectedItem = newItems[newContractType]?.[0];
        if (!newSelectedItem) return;

        setAvailableItems(newItems);
        setContractType(newContractType);
        setSelectedItem(newSelectedItem);
        onChange({ ...newSelectedItem, type: newContractType });
      });
  }, []);

  const containsItems = Settings.features?.displayTypes.length > 1 && 
    (availableItems?.["electricity"]?.length ?? 0) >= 1 &&
    (availableItems?.["gas"]?.length ?? 0) >= 1;

  return ( !(hideIfEmpty && !containsItems) &&
    <Box>
      <Grid container spacing={2}>
        {containsItems &&
          <Grid item xs={12} md={2}>
            <FormControl fullWidth={true}>
              <InputLabel shrink htmlFor="" id="contract-type-label">
                {(t('common:text.contact_type_contract')) + "*"}
              </InputLabel>
              <Select
                labelId="contract-type-label"
                id="contract-type"
                value={contractType}
                onChange={changeContractType}
                style={{textTransform: "capitalize"}}
              >
                <MenuItem value={"electricity"} style={{textTransform: "capitalize"}}>{t('common:content_types.electricity')}</MenuItem>
                <MenuItem value={"gas"} style={{textTransform: "capitalize"}}>{t('common:content_types.gas')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        }

        <Grid item xs={12} md={containsItems ? 10 : 12}>
          {state === "fetching" ? (
            <Skeleton data-testid="skeleton-contracts" height={56} />
          ) :
          (<>
            <FormControl fullWidth disabled={readOnly}>
              <InputLabel shrink htmlFor="" id="contract-name-label">
                {t('common:text.contact_contract') + "*"}
              </InputLabel>
              <Select
                labelId="contract-name-label"
                id="contract-name"
                value={selectedItem?.id.toString() ?? ""}
                onChange={handleChange}
              >
                {availableItems?.[contractType]?.map(contract => (
                  <MenuItem key={contract.id} value={contract.id}>
                    {contract.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContractSelectField;
